import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { BASE_URL } from '@/config/api';

interface SpotlightWizardProps {
  isVisible: boolean;
  targetSelector: string;
  message: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  onClose: () => void;
  padding?: number;
  showCloseButton?: boolean;
  /** When true, clicking the spotlighted target triggers onClose automatically */
  closeOnTargetClick?: boolean;
}

const SpotlightWizard: React.FC<SpotlightWizardProps> = ({
  isVisible,
  targetSelector,
  message,
  position = 'auto',
  onClose,
  padding = 8,
  showCloseButton = true,
  closeOnTargetClick = false,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [maskId] = useState(() => `spotlight-mask-${Math.random().toString(36).slice(2)}`);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { t } = useLanguage();
  const { token, refreshUserData } = useAuth();

  useEffect(() => {
    if (!isVisible) {
      setTargetRect(null);
      return;
    }

    let element: Element | null = null;
    let rafId = 0;

    const isElementUsable = (el: Element): boolean => {
      const rect = el.getBoundingClientRect();
      if (rect.width < 4 || rect.height < 4) return false;
      // Reject elements that are essentially the full page (broken layout / not-yet-laid-out parents)
      if (rect.width > window.innerWidth * 0.9 && rect.height > window.innerHeight * 0.9) return false;
      // Reject hidden elements
      const style = window.getComputedStyle(el as HTMLElement);
      if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') return false;
      // Reject offscreen / negative
      if (rect.bottom < 0 || rect.right < 0) return false;
      if (rect.top > window.innerHeight || rect.left > window.innerWidth) return false;
      return true;
    };

    const updateRect = () => {
      const el = document.querySelector(targetSelector);
      if (el && isElementUsable(el)) {
        element = el;
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
    };

    // Try multiple times until target appears in DOM AND is usable
    let attempts = 0;
    const tryFind = () => {
      const el = document.querySelector(targetSelector);
      if (el && isElementUsable(el)) {
        element = el;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Wait for scroll/layout to settle, then measure
        setTimeout(updateRect, 400);

        if (closeOnTargetClick) {
          el.addEventListener('click', onClose, { once: true });
        }
      } else if (attempts < 40) {
        attempts++;
        setTimeout(tryFind, 150);
      }
    };
    // Defer first attempt slightly to let layout settle on initial mount / F5
    const initialTimer = setTimeout(tryFind, 200);

    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateRect);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      cancelAnimationFrame(rafId);
      if (element && closeOnTargetClick) {
        element.removeEventListener('click', onClose);
      }
    };
  }, [isVisible, targetSelector, closeOnTargetClick, onClose]);

  if (!isVisible || !targetRect) return null;
  // Defensive: skip render if target dimensions look broken (too small or fills the page)
  if (targetRect.width < 4 || targetRect.height < 4) return null;
  if (targetRect.width > window.innerWidth * 0.9 && targetRect.height > window.innerHeight * 0.9) return null;

  // While confirmation dialog is open, hide the spotlight overlay to avoid double-opacity stacking.
  // The AlertDialog itself stays mounted below so the user can confirm/cancel.
  const overlayHidden = confirmOpen;

  const disableWizard = async () => {
    localStorage.setItem('wizard-active', 'false');
    localStorage.setItem('wizard-completed', 'true');
    localStorage.removeItem('wizard-step');
    window.dispatchEvent(new Event('wizard-state-changed'));
    onClose();

    // Persist on server so wizard is never shown again on any device
    if (token) {
      try {
        await fetch(`${BASE_URL}/api/wizard_disable`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
        await refreshUserData?.();
      } catch (e) {
        console.error('Failed to disable wizard on server', e);
      }
    }
  };

  const getTooltipPosition = () => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const messageHeight = 120;
    const messageWidth = 300;

    if (position === 'auto') {
      if (targetRect.bottom + messageHeight + 20 < viewportHeight) return 'bottom';
      if (targetRect.top - messageHeight - 20 > 0) return 'top';
      if (targetRect.right + messageWidth + 20 < viewportWidth) return 'right';
      return 'left';
    }
    return position;
  };

  const tooltipPos = getTooltipPosition();

  const getTooltipStyle = (): React.CSSProperties => {
    const tooltipW = 280;
    const margin = 16;
    const style: React.CSSProperties = { width: tooltipW };

    switch (tooltipPos) {
      case 'bottom':
        style.left = Math.max(8, Math.min(window.innerWidth - tooltipW - 8, targetRect.left + targetRect.width / 2 - tooltipW / 2));
        style.top = targetRect.bottom + margin;
        break;
      case 'top':
        style.left = Math.max(8, Math.min(window.innerWidth - tooltipW - 8, targetRect.left + targetRect.width / 2 - tooltipW / 2));
        style.top = Math.max(8, targetRect.top - 120 - margin);
        break;
      case 'right':
        style.left = targetRect.right + margin;
        style.top = targetRect.top + targetRect.height / 2 - 50;
        break;
      case 'left':
        style.left = Math.max(8, targetRect.left - tooltipW - margin);
        style.top = targetRect.top + targetRect.height / 2 - 50;
        break;
    }
    return style;
  };

  const x = targetRect.left - padding;
  const y = targetRect.top - padding;
  const w = targetRect.width + padding * 2;
  const h = targetRect.height + padding * 2;

  return (
    <>
      {/* SVG mask overlay: dark everywhere EXCEPT spotlight area. The mask intercepts clicks outside the spotlight, so only the highlighted target is interactive. */}
      {!overlayHidden && (
        <>
          {/* Click blocker: 4 invisible divs around the spotlight that swallow pointer events */}
          <div
            className="fixed pointer-events-auto"
            style={{ zIndex: 9998, left: 0, top: 0, right: 0, height: Math.max(0, y) }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="fixed pointer-events-auto"
            style={{ zIndex: 9998, left: 0, top: y + h, right: 0, bottom: 0 }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="fixed pointer-events-auto"
            style={{ zIndex: 9998, left: 0, top: y, width: Math.max(0, x), height: h }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="fixed pointer-events-auto"
            style={{ zIndex: 9998, left: x + w, top: y, right: 0, height: h }}
            onClick={(e) => e.stopPropagation()}
          />
          <svg
            className="fixed inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 9998 }}
            width="100%"
            height="100%"
          >
            <defs>
              <mask id={maskId}>
                <rect width="100%" height="100%" fill="white" />
                <rect x={x} y={y} width={w} height={h} rx="8" ry="8" fill="black" />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.65)"
              mask={`url(#${maskId})`}
            />
          </svg>

          {/* Highlight border around target */}
          <div
            className="fixed pointer-events-none border-2 border-white rounded-lg animate-pulse"
            style={{
              left: x,
              top: y,
              width: w,
              height: h,
              zIndex: 9999,
              boxShadow: '0 0 0 2px hsl(var(--primary) / 0.5), 0 0 30px rgba(255,255,255,0.6)',
            }}
          />

          {/* Close button */}
          <button
            onClick={() => setConfirmOpen(true)}
            className="fixed top-6 right-6 w-10 h-10 rounded-full bg-background hover:bg-muted flex items-center justify-center shadow-lg transition-colors"
            style={{ zIndex: 10001 }}
            aria-label="Close wizard"
          >
            <X size={20} className="text-foreground" />
          </button>

          {/* Tooltip message */}
          <motion.div
            className="fixed bg-background rounded-lg shadow-2xl p-4"
            style={{ ...getTooltipStyle(), zIndex: 10000 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
          >
            <p className="text-sm font-medium text-foreground">{message}</p>
            {showCloseButton && (
              <div className="flex justify-end mt-3">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded hover:opacity-90 transition-opacity font-medium"
                >
                  OK
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('wizard.confirmStopTitle') || 'Arrêter la visite guidée ?'}
            </AlertDialogTitle>
            <AlertDialogDescription className='pb-5'>
              {t('wizard.confirmStopDesc') || 'Vous pouvez la relancer plus tard depuis votre compte.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel') || 'Annuler'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                disableWizard();
              }}
            >
              {t('wizard.confirmStopAction') || 'Arrêter'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SpotlightWizard;
